import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CameraOutlined, CloseOutlined, ExclamationCircleOutlined, SearchOutlined, UploadOutlined, ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Col, Input, Progress, Result, Row, Spin, Typography } from "antd";
import Dragger from "antd/lib/upload/Dragger";
import { useEffect, useMemo, useRef, useState } from "react";
import { STORAGE_FILE_TYPES_IMAGE } from "@devowl-wp/api";
import { EUserRole, routeLocationMediaPost } from "@devowl-wp/api-real-cookie-banner";
import { useScrollToBottomAction } from "@devowl-wp/react-utils";
import { config } from "../../../../config/config.js";
import { useMedia } from "../../../../hooks/useMedia.js";
import { useMediaDelete } from "../../../../hooks/useMediaDelete.js";
import { useTranslation } from "../../../../others/i18n.js";
import { useStores } from "../../../../store/root.js";
import { spacing } from "../../../../styles/spacing.js";
import { QueryKeys } from "../../../../util/query-keys.js";
import { ForbiddenInfo } from "../../forbidden.js";
import { ImageWithButtons } from "./custom/image-with-buttons.js";
/**
 * Media Manager
 *
 * Upload, view and search for media in Real Cookie Banner.
 */ function MediaManager(settings) {
    const { t } = useTranslation();
    const ref = useRef();
    const { rcbAuthenticationStore, notificationStore } = useStores();
    const queryClient = useQueryClient();
    const { isPreloadEnabled } = settings;
    // Permission Checks
    const hasRole = rcbAuthenticationStore.hasRole(EUserRole.Admin);
    if (!hasRole) {
        return /*#__PURE__*/ _jsx(ForbiddenInfo, {});
    }
    // Media data relevant
    const [searchTerm, setSearchTerm] = useState("");
    const mediaQuery = useMedia(isPreloadEnabled, searchTerm);
    const searchElement = useRef(null);
    // Upload status
    const [isUploadVisible, setUploadVisible] = useState(false);
    const [isUploading, setUploading] = useState(false);
    const [uploadingProgress, setUploadingProgress] = useState(0);
    // fetches new image pages, when bottom is reached.
    useScrollToBottomAction(ref, ()=>{
        if (!mediaQuery.isFetchingNextPage && isPreloadEnabled) {
            mediaQuery.fetchNextPage();
        }
    }, 30);
    // Upload area configuration properties
    const uploadProps = useMemo(()=>{
        return {
            action: `${config.environment.hosts.api.realCookieBanner}/${config.environment.hosts.api.realCookieBannerApiVersion}${routeLocationMediaPost.path}`,
            multiple: true,
            directory: false,
            listType: "picture-card",
            name: "files",
            headers: {
                authorization: `Bearer ${rcbAuthenticationStore?.session?.jwt}`
            },
            showUploadList: false,
            onChange: (info)=>{
                const processing = info.fileList.filter((file)=>file.status === "uploading");
                if (processing.length === 0) {
                    queryClient.invalidateQueries(QueryKeys.media());
                    setUploading(false);
                    setSearchTerm("");
                    info.fileList.filter((file)=>file.status === "done").map((file)=>notificationStore.add({
                            type: "success",
                            message: t("{{fileName}} successfully uploaded.", {
                                fileName: file.name
                            }),
                            closable: true,
                            duration: 3.5
                        }));
                    info.fileList.length = 0;
                } else {
                    if (!isUploading) {
                        setUploading(true);
                    }
                    setUploadingProgress(Math.floor((info.fileList.length - processing.length) / info.fileList.length * 100));
                }
            },
            beforeUpload: (file)=>{
                const fileType = file.type.split("/");
                const fileTypeName = fileType[fileType.length - 1];
                const isSupported = STORAGE_FILE_TYPES_IMAGE.map((type)=>type.toString()).indexOf(fileTypeName) >= 0;
                if (!isSupported) {
                    notificationStore.add({
                        message: t(`Unsupported media type: {{fileName}} `, {
                            fileName: file.name
                        }),
                        type: "error",
                        duration: 10,
                        closable: true
                    });
                }
                return isSupported;
            },
            style: {
                padding: "1rem",
                width: "100%",
                textAlign: "center"
            },
            prefixCls: process.env.ANTD_PREFIX
        };
    }, [
        rcbAuthenticationStore
    ]);
    // ANTD UI components
    const { Search } = Input;
    const { Title } = Typography;
    const [mediaDelete, setMediaDelete] = useState(undefined);
    const [deletedIds, setDeletedIds] = useState([]);
    const mediaDeleteQuery = useMediaDelete(mediaDelete);
    const onDelete = (media)=>{
        setMediaDelete(media);
    };
    useEffect(()=>{
        const { error, isError, isSuccess } = mediaDeleteQuery;
        if (isSuccess && mediaDelete) {
            notificationStore.add({
                type: "success",
                message: t(`Successfully deleted ${mediaDelete.file.name}`),
                duration: 5
            });
            setDeletedIds([
                ...deletedIds,
                mediaDelete.id
            ]);
            setMediaDelete(undefined);
            mediaQuery.refetch();
        } else if (isError) {
            notificationStore.addFromServerError(error, t);
        }
    }, [
        mediaDeleteQuery
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        ref: ref,
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                children: [
                    /*#__PURE__*/ _jsx(Col, {
                        children: /*#__PURE__*/ _jsx(Title, {
                            style: {
                                float: "left"
                            },
                            level: 3,
                            children: t("Media")
                        })
                    }),
                    /*#__PURE__*/ _jsx(Col, {
                        flex: 1,
                        children: /*#__PURE__*/ _jsx(Button, {
                            style: {
                                float: "left",
                                marginLeft: "1rem"
                            },
                            onClick: ()=>setUploadVisible(!isUploadVisible),
                            children: isUploadVisible ? t("Hide upload") : t("Show upload")
                        })
                    }),
                    /*#__PURE__*/ _jsx(Col, {
                        flex: 3,
                        style: {
                            textAlign: "center"
                        }
                    }),
                    /*#__PURE__*/ _jsx(Col, {
                        flex: 1,
                        style: {
                            textAlign: "right",
                            float: "right"
                        },
                        children: /*#__PURE__*/ _jsx(Search, {
                            style: {
                                textAlign: "left"
                            },
                            placeholder: t("Search image ..."),
                            onSearch: (searchTerm)=>{
                                setSearchTerm(searchTerm);
                            },
                            maxLength: 64,
                            max: 64,
                            enterButton: true,
                            loading: mediaQuery.isFetching,
                            ref: searchElement,
                            autoFocus: true
                        })
                    })
                ]
            }),
            isUploadVisible && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Row, {
                        children: /*#__PURE__*/ _jsx(Col, {
                            flex: 1,
                            children: /*#__PURE__*/ _jsx(Spin, {
                                spinning: isUploading,
                                children: /*#__PURE__*/ _jsx(Dragger, {
                                    ...uploadProps,
                                    disabled: mediaQuery.isFetching,
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsxs("p", {
                                                className: `${process.env.ANTD_PREFIX}-ant-upload-drag-icon`,
                                                children: [
                                                    /*#__PURE__*/ _jsx(UploadOutlined, {}),
                                                    " ",
                                                    t("Upload")
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: `${process.env.ANTD_PREFIX}-ant-upload-text`,
                                                children: t("Click or drag file to this area to upload")
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: `${process.env.ANTD_PREFIX}-ant-upload-hint`,
                                                children: t("Support for a single or bulk upload. Strictly prohibit from uploading company data or other band files")
                                            })
                                        ]
                                    })
                                })
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(Row, {
                        children: /*#__PURE__*/ _jsx(Col, {
                            flex: 1,
                            children: isUploading && /*#__PURE__*/ _jsx(Progress, {
                                strokeColor: "green",
                                percent: uploadingProgress
                            })
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Row, {
                style: {
                    minHeight: "10rem",
                    overflowY: "auto",
                    height: settings.onVisibleChange ? "calc(100vh - 25rem)" : "auto"
                },
                children: /*#__PURE__*/ _jsx(Col, {
                    flex: 1,
                    style: {
                        textAlign: "left"
                    },
                    children: mediaQuery.data && mediaQuery?.data?.pages[0]?.data?.length > 0 ? mediaQuery.data.pages.map((response)=>response.data.map((media)=>/*#__PURE__*/ _jsx(ImageWithButtons, {
                                hidden: deletedIds.includes(media?.id),
                                isBusy: media.id === mediaDelete?.id,
                                onDoubleClick: ()=>{
                                    settings.onVisibleChange?.(false);
                                },
                                onClick: ()=>{
                                    settings.onChange?.(media?.file);
                                },
                                styles: {
                                    popup: {
                                        mask: {
                                            backgroundColor: "#000000e6"
                                        }
                                    }
                                },
                                preview: !settings.onVisibleChange ? {
                                    icons: {
                                        rotateLeft: /*#__PURE__*/ _jsx(_Fragment, {}),
                                        rotateRight: /*#__PURE__*/ _jsx(_Fragment, {}),
                                        close: /*#__PURE__*/ _jsx(CloseOutlined, {}),
                                        zoomIn: /*#__PURE__*/ _jsx(ZoomInOutlined, {}),
                                        zoomOut: /*#__PURE__*/ _jsx(ZoomOutOutlined, {})
                                    }
                                } : false,
                                width: 200,
                                height: 200,
                                src: media?.file?.downloadUrl,
                                style: {
                                    padding: spacing.layout.image.padding,
                                    objectFit: "contain",
                                    backgroundColor: media?.file.key === settings?.value?.key ? "lightskyblue" : "",
                                    borderRadius: spacing.layout.image.borderRadius
                                },
                                fallback: "data:image/png;base64,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",
                                deleteOptions: {
                                    onDelete
                                },
                                media: media
                            }, media.id))) : !mediaQuery.isFetching && searchTerm ? /*#__PURE__*/ _jsx(Result, {
                        style: {
                            opacity: 0.5
                        },
                        icon: /*#__PURE__*/ _jsx(SearchOutlined, {}),
                        title: t(`"{{searchTerm}}" not found`, {
                            searchTerm
                        })
                    }) : !mediaQuery.isError && !mediaQuery.isFetching ? /*#__PURE__*/ _jsx(Result, {
                        style: {
                            opacity: 0.5
                        },
                        icon: /*#__PURE__*/ _jsx(CameraOutlined, {}),
                        title: t(`Upload some pictures!`)
                    }) : mediaQuery.isError && /*#__PURE__*/ _jsx(Result, {
                        status: "error",
                        style: {
                            opacity: 0.5
                        },
                        icon: /*#__PURE__*/ _jsx(ExclamationCircleOutlined, {}),
                        title: t(`Error occurred!`),
                        subTitle: `${mediaQuery.error.code} - ${mediaQuery.error?.message}`
                    })
                })
            }),
            /*#__PURE__*/ _jsx(Row, {
                children: /*#__PURE__*/ _jsxs(Col, {
                    style: {
                        textAlign: "center",
                        width: "100%",
                        paddingTop: "1rem",
                        borderTop: "1px solid #f0f0f0"
                    },
                    children: [
                        mediaQuery.data?.pages?.map((page)=>page.data.length).reduce((pre, curr)=>pre + curr),
                        " ",
                        t(" image(s) loaded"),
                        /*#__PURE__*/ _jsx("br", {}),
                        mediaQuery.hasNextPage && /*#__PURE__*/ _jsx(Button, {
                            type: "primary",
                            onClick: ()=>mediaQuery.fetchNextPage(),
                            children: t("Load more...")
                        })
                    ]
                })
            })
        ]
    });
}
export { MediaManager };
